import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader, Card, money, StatusBadge, Th, Td, DateRangeFilter, rangeParams } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const OfferForm = ({ initial, networks, onSave }) => {
  const [form, setForm] = useState(
    initial || { name: "", network_id: "", url: "", payout_type: "CPA", payout: 0, daily_cap: 0, geo: [], status: "active", redirect_mode: "interstitial", notes: "" }
  );
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs">Offer Name</Label>
        <Input data-testid="offer-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" />
      </div>
      <div>
        <Label className="text-xs">Affiliate Network</Label>
        <Select value={form.network_id} onValueChange={(v) => set("network_id", v)}>
          <SelectTrigger data-testid="offer-network-select" className="mt-1"><SelectValue placeholder="Select network" /></SelectTrigger>
          <SelectContent>{networks.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Offer URL (use {"{click_id}"} macro)</Label>
        <Input data-testid="offer-url-input" value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://network.com/offer?subid={click_id}" className="mt-1 font-mono text-xs" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Payout Type</Label>
          <Select value={form.payout_type} onValueChange={(v) => set("payout_type", v)}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{["CPA", "CPL", "CPS", "RevShare"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Payout ($)</Label>
          <Input data-testid="offer-payout-input" type="number" step="0.01" value={form.payout} onChange={(e) => set("payout", parseFloat(e.target.value) || 0)} className="mt-1" />
        </div>
        <div>
          <Label className="text-xs">Daily Cap</Label>
          <Input type="number" value={form.daily_cap} onChange={(e) => set("daily_cap", parseInt(e.target.value) || 0)} className="mt-1" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Redirect Method</Label>
        <Select value={["direct", "referrer"].includes(form.redirect_mode) ? form.redirect_mode : "interstitial"} onValueChange={(v) => set("redirect_mode", v)}>
          <SelectTrigger data-testid="offer-redirect-mode-select" className="mt-1"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="interstitial">Safe Redirect Page (recommended — hides referrer)</SelectItem>
            <SelectItem value="referrer">Direct + Send Website Referrer (network sees the website the click came from)</SelectItem>
            <SelectItem value="direct">Direct Instant (no referrer sent)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button data-testid="offer-save-button" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!form.name || !form.network_id || !form.url} onClick={() => onSave(form)}>
        {initial ? "Update Offer" : "Create Offer"}
      </Button>
    </div>
  );
};

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rangeSel, setRangeSel] = useState({ range: "this_month" });

  const load = useCallback(() => {
    Promise.all([api.get("/offers"), api.get("/networks"), api.get(`/stats/entities?${rangeParams(rangeSel)}`)]).then(([o, n, st]) => {
      setOffers(o.data); setNetworks(n.data); setStats(st.data);
    });
  }, [rangeSel]);
  useEffect(load, [load]);

  const save = async (form) => {
    if (editing) await api.put(`/offers/${editing.id}`, form);
    else await api.post("/offers", form);
    toast.success(editing ? "Offer updated" : "Offer created");
    setOpen(false); setEditing(null); load();
  };

  const del = async (id) => { await api.delete(`/offers/${id}`); toast.success("Offer deleted"); load(); };

  return (
    <div data-testid="offers-page">
      <PageHeader
        title="Offers"
        subtitle="Affiliate offers, payouts and daily conversion caps"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-offer-button" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1" /> New Offer</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Offer" : "Create Offer"}</DialogTitle></DialogHeader>
              <OfferForm key={editing?.id || "new"} initial={editing} networks={networks} onSave={save} />
            </DialogContent>
          </Dialog>
        }
      />
      <div className="mb-4 fade-up"><DateRangeFilter value={rangeSel} onChange={setRangeSel} /></div>

      <Card className="overflow-hidden fade-up">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr><Th>Offer</Th><Th>Network</Th><Th>Type</Th><Th className="text-right">Payout</Th><Th>Geo</Th><Th className="text-right">Clicks</Th><Th className="text-right">Conv.</Th><Th className="text-right">Revenue</Th><Th>Cap Usage</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr></thead>
          <tbody data-testid="offers-table-body">
            {offers.map((o) => {
              const st = stats[o.id] || { clicks: 0, conversions: 0, revenue: 0 };
              const capPct = o.daily_cap ? Math.min(100, Math.round((st.conversions / 30 / o.daily_cap) * 100)) : 0;
              return (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <Td className="font-medium text-slate-900">{o.name}<div className="font-mono text-[10px] text-slate-400 truncate max-w-[220px]">{o.url}</div></Td>
                  <Td className="text-xs">{networks.find((n) => n.id === o.network_id)?.name || "—"}</Td>
                  <Td><span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-semibold">{o.payout_type}</span></Td>
                  <Td className="text-right font-mono font-semibold">{money(o.payout)}</Td>
                  <Td className="text-xs font-mono">{(o.geo || []).join(", ") || "All"}</Td>
                  <Td className="text-right font-mono">{st.clicks.toLocaleString()}</Td>
                  <Td className="text-right font-mono">{st.conversions}</Td>
                  <Td className="text-right font-mono font-semibold text-emerald-600">{money(st.revenue)}</Td>
                  <Td>
                    <div className="w-20">
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${capPct > 80 ? "bg-rose-500" : capPct > 50 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${capPct}%` }} />
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{capPct}% of {o.daily_cap}/day</div>
                    </div>
                  </Td>
                  <Td><StatusBadge status={o.status} /></Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(o); setOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => del(o.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {offers.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No offers yet.</div>}
      </Card>
    </div>
  );
}
